import React, { useState, useRef, useEffect, Fragment } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/** Flatten React children so SelectItems wrapped in <>...</> are visible to display + list logic. */
const flattenSelectChildren = (children) => {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === Fragment) {
      out.push(...flattenSelectChildren(child.props.children));
    } else {
      out.push(child);
    }
  });
  return out;
};

// Simple, functional Select component
export const FunctionalSelect = ({ 
  value, 
  onValueChange, 
  placeholder = "All",
  children,
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue) => {
    onValueChange(newValue);
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  // Find the display text for the current value
  const getDisplayText = () => {
    if (!value && value !== 0) return placeholder;

    const selectedChild = flattenSelectChildren(children).find(
      (child) =>
        React.isValidElement(child) &&
        child.type === SelectItem &&
        String(child.props.value) === String(value)
    );

    return selectedChild ? selectedChild.props.children : placeholder;
  };

  return (
    <div className={`relative ${className}`} ref={selectRef}>
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={toggleOpen}
        disabled={disabled}
      >
        <span className="block truncate">
          {getDisplayText()}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {flattenSelectChildren(children).map((child, idx) => {
            if (React.isValidElement(child) && child.type === SelectItem) {
              return React.cloneElement(child, {
                key: child.key ?? child.props.value ?? idx,
                isSelected: String(child.props.value) === String(value),
                onSelect: handleSelect
              });
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
};

export const SelectItem = ({ className = '', children, value, onSelect, isSelected, ...props }) => (
  <div
    className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${className}`}
    onClick={() => onSelect(value)}
    {...props}
  >
    {isSelected && (
      <Check className="absolute left-2 h-4 w-4" />
    )}
    {children}
  </div>
);

// Legacy exports for backward compatibility
export const Select = FunctionalSelect;
export const SelectTrigger = () => null;
export const SelectContent = () => null;
export const SelectValue = () => null;
